import { Box, Button, ModalProps } from "@chakra-ui/react";
import useTranslate from "hooks/useTranslate";
import TraitTile from "components/TraitTile";
import { SwiperSlide } from "swiper/react/swiper-react";
import { Controller } from "swiper";
import BeforeAfterPreview from "components/BeforeAfterPreview";
import ControlledSwiper from "components/ControlledSwiper";
import useTraits from "hooks/useTraits";
import useStakeTraits from "hooks/useStakeTraits";
import useIsTraitStakingAapprovedForAll from "hooks/useIsTraitStakingAapprovedForAll";
import useConnectedAccount from "hooks/useConnectedAccount";
import { traitStakingContract } from "config";
import useApproveTraits from "hooks/useApproveTraits";
import SimplePopup from "components/SimplePopup";

type StakePopupProps = Omit<ModalProps, "children"> & {
  currentTraits: number[];
  traitsToStake: number[];
  tokenId: number;
  onSuccess: () => void;
};

const StakePopup = ({
  tokenId,
  currentTraits,
  traitsToStake,
  onSuccess,
  ...rest
}: StakePopupProps) => {
  const translate = useTranslate();
  const [address] = useConnectedAccount();
  const { traitsByTokenId } = useTraits();
  const isMultiple = traitsToStake.length > 1;
  const slidesPerView = isMultiple ? 1.35 : 1;
  const [isApproved, readIsApproved] =
    useIsTraitStakingAapprovedForAll(address);
  const [approve, isApproving] = useApproveTraits(
    traitStakingContract.address,
    readIsApproved
  );

  const [stake, isStaking] = useStakeTraits(tokenId, traitsToStake, onSuccess);

  return (
    <SimplePopup
      title={translate(
        isMultiple ? "stake:title:multi" : "stake:title:single",
        { count: traitsToStake.length }
      )}
      description={translate("stake:description")}
      size="md"
      {...rest}
    >
      <Box pt="10">
        <BeforeAfterPreview
          currentTraits={currentTraits}
          changedTraits={traitsToStake}
          imagesSize="256"
          bg="dark.400"
        />
      </Box>

      <Box mt="3">
        <ControlledSwiper
          spaceBetween={2}
          threshold={10}
          modules={[Controller]}
          slidesPerView={slidesPerView}
          freeMode
        >
          {traitsToStake.map((tokenId) => {
            const trait = traitsByTokenId[tokenId];
            return trait ? (
              <SwiperSlide key={tokenId}>
                <TraitTile
                  tokenId={tokenId}
                  name={trait.name}
                  w="full"
                  canvasSize="12"
                />
              </SwiperSlide>
            ) : null;
          })}
        </ControlledSwiper>
      </Box>

      <Box pt="6">
        {isApproved === undefined ? (
          <Button w="full" colorScheme="dark" isLoading />
        ) : (
          <>
            {isApproved ? (
              <Button
                colorScheme="secondary"
                w="full"
                onClick={() => stake()}
                isLoading={isStaking}
              >
                {translate("stake:submit")}
              </Button>
            ) : (
              <Button
                w="full"
                onClick={() => approve()}
                isLoading={isApproved === undefined || isApproving}
              >
                {translate("stake:approve")}
              </Button>
            )}
          </>
        )}
      </Box>
    </SimplePopup>
  );
};

export default StakePopup;
